import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Radio, message, Space, Tag, Tabs } from 'antd';
import dayjs from 'dayjs';
import AV from 'leancloud-storage';

export const handleEditSubmit = async ({
  selectedExam,
  editingStudent,
  editResult,
  setSelectedExam,
  setIsEditModalVisible,
  setEditingStudent,
  setEditResult,
  setLoading,
  setPendingExams,
  onSuccess
}) => {
  try {
    setLoading(true);
    
    const examQuery = new AV.Query('ExamBooking');
    const exam = await examQuery.get(selectedExam.objectId);
    
    let passedCount = exam.get('passedCount');
    const students = exam.get('students');
    
    const updatedStudents = students.map(student => {
      if (student.studentId === editingStudent.studentId) {
        if (student.result === 'passed') passedCount--;
        if (editResult === 'passed') passedCount++;
        
        return {
          ...student,
          result: editResult,
          status: editResult
        };
      }
      return student;
    });

    exam.set('students', updatedStudents);
    exam.set('passedCount', passedCount);

    const studentQuery = new AV.Query('Student');
    const studentObj = await studentQuery.get(editingStudent.studentId);
    
    const examNumber = getExamNumber(selectedExam.examType);
    const examField = `exam${examNumber}`;
    const examCompletedField = `subject${examNumber}Completed`;
    
    studentObj.set(examField, {
      passed: editResult === 'passed',
      date: dayjs(selectedExam.date.iso || selectedExam.date).format('YYYY-MM-DD')
    });
    
    studentObj.set(examCompletedField, editResult === 'passed');

    const exams = studentObj.get('exams') || [];
    const examIndex = exams.findIndex(e => 
      e.examType === examNumber && 
      dayjs(e.date).isSame(dayjs(selectedExam.date.iso || selectedExam.date), 'day')
    );
    
    if (examIndex !== -1) {
      exams[examIndex] = {
        ...exams[examIndex],
        passed: editResult === 'passed'
      };
    }
    
    studentObj.set('exams', exams);

    await Promise.all([exam.save(), studentObj.save()]);
    
    // Update local state directly
    setSelectedExam(prev => ({
      ...prev,
      students: updatedStudents,
      passedCount
    }));

    // Update the pendingExams list if it exists
    if (setPendingExams) {
      setPendingExams(prev => 
        prev.map(exam => 
          exam.objectId === selectedExam.objectId 
            ? { ...exam, students: updatedStudents, passedCount }
            : exam
        )
      );
    }
    
    message.success('考试结果修改成功');
    setIsEditModalVisible(false);
    setEditingStudent(null);
    setEditResult(null);
    
    // Call onSuccess callback if provided
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    console.error('Edit result error:', error);
    message.error('修改考试结果失败');
  } finally {
    setLoading(false);
  }
};

export const handleSubmitResults = async ({
  selectedExam,
  results,
  setIsModalVisible,
  setLoading,
  onSuccess
}) => {
  try {
    setLoading(true);
    
    const gradedStudentIds = Object.keys(results).filter(id => results[id] !== null);
    
    if (gradedStudentIds.length === 0) {
      message.warning('请至少为一名学员填写考试结果');
      return;
    }

    const examQuery = new AV.Query('ExamBooking');
    const exam = await examQuery.get(selectedExam.objectId);
    
    let passedCount = exam.get('passedCount') || 0;
    let completedCount = exam.get('completedCount') || 0;
    
    const allStudents = exam.get('students');
    const updatedStudents = allStudents.map(student => {
      if (gradedStudentIds.includes(student.studentId)) {
        const result = results[student.studentId];
        if (result === 'passed') passedCount++;
        completedCount++;
        return {
          ...student,
          result,
          status: result
        };
      }
      return student;
    });

    exam.set('students', updatedStudents);
    exam.set('completedCount', completedCount);
    exam.set('passedCount', passedCount);
    
    if (updatedStudents.every(student => student.status === 'completed')) {
      exam.set('status', 'completed');
    }

    // Update each graded student's exam history
    const studentUpdates = gradedStudentIds.map(async (studentId) => {
      const studentQuery = new AV.Query('Student');
      const studentObj = await studentQuery.get(studentId);
      const result = results[studentId];
      
      const examNumber = getExamNumber(selectedExam.examType);
      const examField = `exam${examNumber}`;
      const examCompletedField = `subject${examNumber}Completed`;
      
      studentObj.set(examField, {
        passed: result === 'passed',
        date: dayjs(selectedExam.date.iso || selectedExam.date).format('YYYY-MM-DD')
      });
      
      if (result === 'passed') {
        studentObj.set(examCompletedField, true);
      }

      const exams = studentObj.get('exams') || [];
      exams.push({
        examType: examNumber,
        date: dayjs(selectedExam.date.iso || selectedExam.date).format('YYYY-MM-DD'),
        passed: result === 'passed',
        examiner: selectedExam.examiner,
        createdAt: new Date()
      });
      studentObj.set('exams', exams);

      return studentObj.save();
    });

    await Promise.all([exam.save(), ...studentUpdates]);
    
    message.success('考试结果提交成功');
    setIsModalVisible(false);
    
    // Call onSuccess callback if provided
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    console.error('Submit results error:', error);
    message.error('考试结果提交失败');
  } finally {
    setLoading(false);
  }
};

export const getExamNumber = (examType) => {
  const mapping = {
    '科目一': '1',
    '科目二': '2',
    '科目三': '3',
    '科目四': '4'
  };
  return mapping[examType] || examType.slice(-1);
};

const ExamSummary = () => {
  const [loading, setLoading] = useState(false);
  const [pendingExams, setPendingExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [results, setResults] = useState({});
  const [editResult, setEditResult] = useState(null);

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    try {
      setLoading(true);
      // Fetch pending exams
      const pendingQuery = new AV.Query('ExamBooking');
      pendingQuery.equalTo('status', 'pending');
      pendingQuery.include('createdBy');
      pendingQuery.ascending('date');
      const pendingResults = await pendingQuery.find();

      // Fetch completed exams (last 30 days)
      const completedQuery = new AV.Query('ExamBooking');
      completedQuery.equalTo('status', 'completed');
      completedQuery.include('createdBy');
      completedQuery.greaterThan('date', dayjs().subtract(30, 'days').toDate());
      completedQuery.ascending('date');

      const mapExam = exam => ({
        objectId: exam.id,
        date: exam.get('date'),
        examType: exam.get('examType'),
        examiner: exam.get('examiner'),
        students: exam.get('students'),
        status: exam.get('status'),
        createdBy: exam.get('createdBy')?.get('username'),
        totalCount: exam.get('totalCount'),
        completedCount: exam.get('completedCount'),
        passedCount: exam.get('passedCount')
      });

      setPendingExams(pendingResults.map(mapExam));
    } catch (error) {
      console.error('Fetch exams error:', error);
      message.error('获取考试记录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleGrade = (exam) => {
    setSelectedExam(exam);
    // Initialize results only for pending students
    const initialResults = {};
    exam.students
      .filter(student => student.status === 'pending')
      .forEach(student => {
        initialResults[student.studentId] = null;
      });
    setResults(initialResults);
    setIsModalVisible(true);
  };

  const handleResultChange = (studentId, result) => {
    setResults(prev => ({
      ...prev,
      [studentId]: result
    }));
  };

  const handleEditGrade = (exam, student) => {
    setSelectedExam(exam);
    setEditingStudent(student);
    setEditResult(student.result);
    setIsEditModalVisible(true);
  };

  const columns = [
    {
      title: '考试日期',
      dataIndex: 'date',
      key: 'date',
      render: date => dayjs(date.iso || date).format('YYYY-MM-DD')
    },
    {
      title: '考试科目',
      dataIndex: 'examType',
      key: 'examType'
    },
    {
      title: '考官',
      dataIndex: 'examiner',
      key: 'examiner'
    },
    {
      title: '考试人数',
      dataIndex: 'totalCount',
      key: 'totalCount'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button 
          type="primary" 
          onClick={() => handleGrade(record)}
        >
          {record.status === 'completed' ? '查看成绩' : '录入成绩'}
        </Button>
      )
    }
  ];

  const studentColumns = [
    {
      title: '档案号',
      dataIndex: 'fileId',
      key: 'fileId'
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '身份证号',
      dataIndex: 'uniqueId',
      key: 'uniqueId'
    },
    {
      title: '考试结果',
      key: 'result',
      render: (_, student) => {
        if (student.status === 'completed') {
          return (
            <Tag color={
              student.result === 'passed' ? 'success' :
              student.result === 'failed' ? 'error' :
              'default'
            }>
              {student.result === 'passed' ? '通过' :
               student.result === 'failed' ? '不通过' :
               '缺考'}
            </Tag>
          );
        }
        return (
          <Radio.Group
            value={results[student.studentId]}
            onChange={e => handleResultChange(student.studentId, e.target.value)}
          >
            <Space>
              <Radio.Button 
                value="passed"
                style={{ 
                  backgroundColor: results[student.studentId] === 'passed' ? '#f6ffed' : undefined,
                  borderColor: results[student.studentId] === 'passed' ? '#b7eb8f' : undefined
                }}
              >
                通过
              </Radio.Button>
              <Radio.Button 
                value="failed"
                style={{ 
                  backgroundColor: results[student.studentId] === 'failed' ? '#fff2f0' : undefined,
                  borderColor: results[student.studentId] === 'failed' ? '#ffccc7' : undefined
                }}
              >
                不通过
              </Radio.Button>
              <Radio.Button 
                value="absent"
                style={{ 
                  backgroundColor: results[student.studentId] === 'absent' ? '#f5f5f5' : undefined,
                  borderColor: results[student.studentId] === 'absent' ? '#d9d9d9' : undefined
                }}
              >
                缺考
              </Radio.Button>
            </Space>
          </Radio.Group>
        );
      }
    }
  ];

  const renderStudentTable = (status) => {
    const filteredStudents = selectedExam?.students.filter(student => 
      status === 'pending' ? student.status === 'pending' : student.status === 'completed'
    );

    return (
      <Table
        columns={[
          ...studentColumns,
          {
            title: '操作',
            key: 'action',
            render: (_, student) => 
              student.status === 'completed' && (
                <Button 
                  type="link" 
                  onClick={() => handleEditGrade(selectedExam, student)}
                >
                  修改
                </Button>
              )
          }
        ]}
        dataSource={filteredStudents}
        rowKey="studentId"
        pagination={{
          pageSize: 10,
          showTotal: (total) => `共 ${total} 名学员`,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
      />
    );
  };

  return (
    <Card title="考试成绩录入">
      <Table
        columns={columns}
        dataSource={pendingExams}
        rowKey="objectId"
        loading={loading}
      />

      <Modal
        title={`成绩录入 - ${selectedExam?.examType} (${selectedExam ? dayjs(selectedExam.date.iso || selectedExam.date).format('YYYY-MM-DD') : ''})`}
        open={isModalVisible}
        onOk={() => handleSubmitResults({
          selectedExam,
          results,
          setIsModalVisible,
          setLoading,
          onSuccess: fetchExams
        })}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={loading}
        width={800}
      >
        <Tabs
          items={[
            {
              key: 'pending',
              label: '待录入学员',
              children: renderStudentTable('pending')
            },
            {
              key: 'completed',
              label: '已录入学员',
              children: renderStudentTable('completed')
            }
          ]}
        />
      </Modal>

      <Modal
        title={`修改成绩 - ${editingStudent?.name}`}
        open={isEditModalVisible}
        onOk={() => handleEditSubmit({
          selectedExam,
          editingStudent,
          editResult,
          setSelectedExam,
          setIsEditModalVisible,
          setEditingStudent,
          setEditResult,
          setLoading,
          setPendingExams,
          onSuccess: fetchExams
        })}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingStudent(null);
          setEditResult(null);
        }}
        confirmLoading={loading}
      >
        <Radio.Group
          value={editResult}
          onChange={e => setEditResult(e.target.value)}
        >
          <Space>
            <Radio.Button 
              value="passed"
              style={{ 
                backgroundColor: editResult === 'passed' ? '#f6ffed' : undefined,
                borderColor: editResult === 'passed' ? '#b7eb8f' : undefined
              }}
            >
              通过
            </Radio.Button>
            <Radio.Button 
              value="failed"
              style={{ 
                backgroundColor: editResult === 'failed' ? '#fff2f0' : undefined,
                borderColor: editResult === 'failed' ? '#ffccc7' : undefined
              }}
            >
              不通过
            </Radio.Button>
            <Radio.Button 
              value="absent"
              style={{ 
                backgroundColor: editResult === 'absent' ? '#f5f5f5' : undefined,
                borderColor: editResult === 'absent' ? '#d9d9d9' : undefined
              }}
            >
              缺考
            </Radio.Button>
          </Space>
        </Radio.Group>
      </Modal>
    </Card>
  );
};

export default ExamSummary; 